import React from 'react';
import { connect } from 'react-redux';

import { FilterIcon } from '../../../../../../assets/icons/Filter.icon';
import { CloseIcon } from '../../../../../../assets/icons/Close.icon';
import { clearFilterCategories, applyFilterCategories, unselectFilterCategoryOptions } from '../../../../../../actions/searchActions';
import { clearFiltersFromSession } from '../../../../../../utils';
import HospitalExpenseFilter from './HospitalExpenseFilter';
import ProviderFilter from './ProviderFilter';
import AgeRangeFilter from './AgeRangeFilter';
import BenefitsCoverageFilter from './BenefitsCoverageFilter';
import CovidCoverageFilter from './CovidCoverageFilter';

import styles from './floatingStyles.module.css';
import OrderRadioFilter from './OrderRadioFilter';

export const FloatButtonFilter = ({ clearFilterCategories, applyFilterCategories, unselectFilterCategoryOptions, filterCategory, filterProvider }) => {
  const [drawerisOpen, setDrawerisOpen] = React.useState(false);
  const [stagedChanges, setStagedChanges] = React.useState([]);
  const [filterKey, setFilterKey] = React.useState(0);

  const handleStageChange = React.useCallback((change) => {
    setStagedChanges(prev => [...prev, change]);
  }, []);

  const handleCloseWithoutApply = React.useCallback(() => {
    setStagedChanges([]);
    setFilterKey(prev => prev + 1);
    setDrawerisOpen(false);
  }, []);

  const updateURLWithFilters = React.useCallback(() => {
    const currentParams = new URLSearchParams(window.location.search);

    const otherParams = [];
    currentParams.forEach((value, key) => {
      if (key !== 'category' && key !== 'provider') {
        otherParams.push(`${key}=${value}`);
      }
    });

    const categoryIds = [];
    if (filterCategory && Array.isArray(filterCategory)) {
      filterCategory.forEach(cat => {
        if (cat.Categories && Array.isArray(cat.Categories)) {
          cat.Categories.forEach(opt => {
            if (opt.selected) {
              const id = opt.ID || opt.Id || opt.id;
              if (id !== undefined && id !== null) {
                categoryIds.push(id);
              }
            }
          });
        }
      });
    }

    const providerSlugs = [];
    if (filterProvider && Array.isArray(filterProvider)) {
      filterProvider.forEach(provider => {
        if (provider.selected) {
          const slug = provider.slug || provider.name;
          if (slug) {
            providerSlugs.push(slug);
          }
        }
      });
    }

    const allParams = [...otherParams];

    if (categoryIds.length > 0) {
      allParams.push(`category=${categoryIds.join(',')}`);
    }

    if (providerSlugs.length > 0) {
      allParams.push(`provider=${providerSlugs.join(',')}`);
    }

    const newUrl = allParams.length > 0 ? `?${allParams.join('&')}` : window.location.pathname;
    window.history.replaceState(null, null, newUrl);
  }, [filterCategory, filterProvider]);

  const handleApplyFilters = React.useCallback(() => {
    const checkboxChanges = stagedChanges.filter(c => c.source && !c.step);
    const sliderChanges = stagedChanges.filter(c => c.step);

    checkboxChanges.forEach(change => {
      if (change.source === 'provider') {
        unselectFilterCategoryOptions('provider', change.optionIndex, 0);
      } else if (change.source === 'category') {
        unselectFilterCategoryOptions('category', change.categoryIndex, change.optionIndex);
      }
    });

    sliderChanges.forEach(change => {
      const { categoryIndex, step } = change;
      if (step && step.optionIndex !== undefined) {
        unselectFilterCategoryOptions('category', categoryIndex, step.optionIndex);
      }
    });

    applyFilterCategories();
    updateURLWithFilters();
    handleCloseWithoutApply();
  }, [stagedChanges, unselectFilterCategoryOptions, applyFilterCategories, updateURLWithFilters, handleCloseWithoutApply]);

  const handleClearFilters = React.useCallback(() => {
    clearFiltersFromSession();

    const searchParams = window.location.search.replace('?', '').split('&');
    const otherParams = searchParams.filter((p) => !['category=', 'provider='].some((k) => p.startsWith(k)) && p);
    const location = otherParams.length > 0 ? `?${otherParams.join('&')}` : window.location.pathname;
    window.history.replaceState(null, null, location);

    React.startTransition(() => {
      setStagedChanges([]);
      setFilterKey(prev => prev + 1);
      clearFilterCategories();
    });
  }, [clearFilterCategories]);

  React.useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleCloseWithoutApply();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleCloseWithoutApply]);

  React.useEffect(() => {
    if (drawerisOpen) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prevOverflow;
      };
    }
  }, [drawerisOpen]);

  const handleOpenDrawer = React.useCallback(() => {
    setDrawerisOpen(true);
  }, []);

  return (
    <React.Fragment>
      <div className={styles.floatingFilter}>
        <div className={styles.floatingButtonFilter} onClick={handleOpenDrawer}>
          <FilterIcon />
          <span>Filtrar e ordenar</span>
        </div>
      </div>

      <div
        className={`${styles.floatingFilterDrawer} ${drawerisOpen ? styles.open : styles.closed}`}
        aria-hidden={!drawerisOpen}
        role="dialog"
        aria-modal="true"
      >
        <div className={styles.floatingFilterDrawerHeader}>
          <span>Filtrar e ordenar</span>
          <div className={styles.closeIcon} onClick={handleCloseWithoutApply}>
            <CloseIcon size={24} />
          </div>
        </div>

        <div className={styles.floatingFilterDrawerBody} key={filterKey}>
          <OrderRadioFilter />
          <HospitalExpenseFilter
            useStagingMode={true}
            onStageChange={handleStageChange}
          />
          <ProviderFilter
            useStagingMode={true}
            onStageChange={handleStageChange}
          />
          <AgeRangeFilter
            useStagingMode={true}
            onStageChange={handleStageChange}
          />
          <BenefitsCoverageFilter
            useStagingMode={true}
            onStageChange={handleStageChange}
          />
          <CovidCoverageFilter
            useStagingMode={true}
            onStageChange={handleStageChange}
          />
        </div>

        <div className={styles.floatingFilterDrawerFooter}>
          <button type="button" className={styles.clearButton} onClick={handleClearFilters}>
            Limpar filtros
          </button>
          <button
            type="button"
            className={styles.applyButton}
            onClick={handleApplyFilters}
          >
            Aplicar filtros
          </button>
        </div>
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = (state) => ({
  filterCategory: state.search.filterCategory,
  filterProvider: state.search.filterProvider,
});

const ConnectedFloatingFilter = connect(mapStateToProps, {
  clearFilterCategories,
  applyFilterCategories,
  unselectFilterCategoryOptions
})(FloatButtonFilter);

export const FloatingFilter = ConnectedFloatingFilter;
export default ConnectedFloatingFilter;



// WEBPACK FOOTER //
// ./src/views/v2/search/components/filter/components/FloatingFilter.jsx